import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PracticeRunner from "@/components/theory/PracticeRunner";
import { ChevronDown, ChevronRight, BookOpen, Play, AlertTriangle, GraduationCap } from "lucide-react";

const applies = (tags, mine) => !tags || tags.length === 0 || tags.some((t) => mine.includes(t));

export default function TheoryStudy({ trainee, disciplines = [], onProgress }) {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [expanded, setExpanded] = useState({});     // sectionId -> lessons[]
  const [revision, setRevision] = useState([]);
  const [study, setStudy] = useState(null);          // lesson currently open in the practice dialog

  const loadRevision = useCallback(async () => {
    try { const r = await api.get(`/theory/trainees/${trainee.id}/revision`); setRevision(r.data || []); } catch { /* best effort */ }
  }, [trainee.id]);

  const load = useCallback(async () => {
    try { const r = await api.get("/theory/sections"); setSections(r.data || []); } catch { toast.error("Failed to load theory"); }
    loadRevision();
  }, [loadRevision]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleSection = async (s) => {
    if (expanded[s.id]) { setExpanded((e) => ({ ...e, [s.id]: null })); return; }
    try { const r = await api.get(`/theory/sections/${s.id}/lessons`); setExpanded((e) => ({ ...e, [s.id]: r.data || [] })); }
    catch { toast.error("Failed to load lessons"); }
  };

  const onGraded = (res) => { if (res?.overall_passed) onProgress?.(); loadRevision(); };

  const visibleSections = sections.filter((s) => applies(s.disciplines, disciplines));

  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid="theory-study">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50 transition-colors" data-testid="theory-study-toggle" aria-expanded={open}>
        {open ? <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
        <GraduationCap className="h-5 w-5 text-lime-500 shrink-0" />
        <div className="flex-1">
          <h2 className="font-heading font-bold text-lg text-slate-900">Theory study &amp; revision</h2>
          <p className="text-xs text-slate-500 mt-0.5">Work through theory courses, sit practice tests and see priority revision topics</p>
        </div>
        {revision.length > 0 && <Badge className="bg-amber-100 text-amber-700 border border-amber-200 gap-1"><AlertTriangle className="h-3 w-3" /> {revision.length} to revise</Badge>}
      </button>

      {open && (
        <div className="px-5 pb-6 pt-2 border-t border-slate-100 space-y-6" data-testid="theory-study-body">
          {revision.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="revision-panel">
              <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" /> Priority revision</p>
              <ul className="space-y-1.5">
                {revision.map((r) => (
                  <li key={r.question_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`revision-item-${r.question_id}`}>
                    <span className="text-slate-700 min-w-0"><span className="text-slate-400">{r.lesson_title}:</span> {r.prompt}</span>
                    <Badge variant="outline" className={`shrink-0 ${r.fail_count >= 3 ? "border-red-300 text-red-600" : "border-amber-300 text-amber-600"}`}>failed ×{r.fail_count}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            {visibleSections.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No theory courses available for this trainee's disciplines yet.</p>}
            {visibleSections.map((s) => (
              <div key={s.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`study-section-${s.id}`}>
                <button onClick={() => toggleSection(s)} className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-50" data-testid={`study-section-toggle-${s.id}`}>
                  {expanded[s.id] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <BookOpen className="h-4 w-4 text-lime-500" />
                  <span className="font-medium text-slate-900 flex-1">{s.title}</span>
                  <Badge variant="outline" className="text-[10px] text-slate-400">{s.lesson_count} lessons</Badge>
                </button>
                {expanded[s.id] && (
                  <div className="divide-y divide-slate-100 border-t border-slate-100">
                    {(expanded[s.id] || []).filter((l) => applies(l.disciplines, disciplines)).map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`study-lesson-${l.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">{l.title}</p>
                          <p className="text-[11px] text-slate-400">{(l.elements || []).length} elements · {(l.elements || []).reduce((n, e) => n + (e.questions || []).length, 0)} questions</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setStudy(l)} data-testid={`study-lesson-btn-${l.id}`}><Play className="h-3.5 w-3.5 mr-1.5" /> Study &amp; test</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!study} onOpenChange={(o) => !o && setStudy(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="study-practice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{study?.title}</DialogTitle>
            <DialogDescription>{study?.intro || "Work through the questions, or switch on AI voice mode to listen and answer aloud."}</DialogDescription>
          </DialogHeader>
          {study && <PracticeRunner lesson={study} traineeId={trainee.id} onGraded={onGraded} onClose={() => setStudy(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
